import React from "react";
import { Link } from "react-router-dom";
import { Zap, ArrowLeft } from "lucide-react";

// NOTE: This is a general, good-faith SaaS template — not legal advice.
// Replace the [BRACKETED] placeholders and have a lawyer review before launch.
const COMPANY = "BizForge AI";
const CONTACT_EMAIL = "[support@yourbusiness.com]";
const JURISDICTION = "[Your State/Country]";
const LAST_UPDATED = "June 2026";

const SECTIONS = [
  {
    h: "1. Introduction",
    p: [
      `This Privacy Policy explains how ${COMPANY} ("we," "us," or "our"), operated by Monroe Digital Media LLC, collects, uses, and protects your information when you use our platform (the "Service"). By using the Service, you agree to the practices described here.`,
    ],
  },
  {
    h: "2. Information we collect",
    p: [
      "Account information: your name, email address, and password (stored securely as a hash) when you register.",
      "Business data: the content you add to run your business — such as clients, opportunities, invoices, expenses, tasks, and calendar events.",
      "Payment information: when you subscribe to a paid plan, payment details are collected and processed directly by our payment processor, Stripe. We do not store your full card details.",
      "Usage data: technical information such as log data, device and browser type, and how you interact with the Service, collected to keep the Service secure and reliable.",
    ],
  },
  {
    h: "3. How we use your information",
    p: [
      "We use your information to: (a) provide, maintain, and improve the Service; (b) process payments and manage subscriptions; (c) power AI features you request; (d) communicate with you about your account, updates, and support; and (e) protect the security and integrity of the Service.",
    ],
  },
  {
    h: "4. AI features & your data",
    p: [
      "When you use AI-assisted features, relevant content you provide is sent to our AI model providers to generate the requested output. We do not sell your business data, and we do not use your private content to train third-party models beyond what is necessary to deliver the feature you requested.",
    ],
  },
  {
    h: "5. Third-party services",
    p: [
      "We rely on trusted third-party providers to operate the Service, including Stripe (payments) and AI model providers. These providers process data on our behalf and are subject to their own privacy and security commitments.",
    ],
  },
  {
    h: "6. Cookies & tracking",
    p: [
      "We use cookies and similar technologies that are necessary to keep you signed in and to operate the Service. You can control cookies through your browser settings, though disabling them may affect functionality.",
    ],
  },
  {
    h: "7. Data retention",
    p: [
      "We retain your information for as long as your account is active or as needed to provide the Service. When you delete your account, we delete or anonymize your personal data within a reasonable period, except where we are required to retain it by law.",
    ],
  },
  {
    h: "8. Data security",
    p: [
      "We use industry-standard measures to protect your information, including encryption in transit and secure password storage. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
    ],
  },
  {
    h: "9. Your rights",
    p: [
      "Depending on your location, you may have the right to access, correct, export, or delete your personal data, and to object to or restrict certain processing. To exercise these rights, contact us using the details below. You can also update much of your information directly within the Service.",
    ],
  },
  {
    h: "10. Children's privacy",
    p: [
      "The Service is not intended for anyone under 18 years of age (or the age of majority in your jurisdiction). We do not knowingly collect personal information from children.",
    ],
  },
  {
    h: "11. International users",
    p: [
      "Your information may be processed and stored in countries other than your own. By using the Service, you consent to the transfer of your information to those locations, which may have different data protection rules than your country.",
    ],
  },
  {
    h: "12. Changes to this policy",
    p: [
      "We may update this Privacy Policy from time to time. If we make material changes, we will provide reasonable notice (for example, in-app or by email). Your continued use of the Service after changes take effect means you accept the updated policy.",
    ],
  },
  {
    h: "13. Contact us",
    p: [
      `Questions about this Privacy Policy or your data? Contact us at ${CONTACT_EMAIL}. This policy is governed by the laws of ${JURISDICTION}.`,
    ],
  },
];

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background">
      <header className="mx-auto flex max-w-3xl items-center justify-between px-6 py-5">
        <Link to="/" className="flex items-center gap-2.5" data-testid="privacy-home-link">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary"><Zap className="h-5 w-5 text-primary-foreground" strokeWidth={2.5} /></div>
          <span className="font-display text-lg font-bold tracking-tight">BizForge<span className="text-accent">AI</span></span>
        </Link>
        <Link to="/" className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground" data-testid="privacy-back-link">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-8 pb-20">
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Privacy Policy</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>

        <div className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-600 dark:text-amber-400" data-testid="privacy-disclaimer">
          <strong>Please note:</strong> This is a general template provided as a starting point and is not legal advice.
          Replace the bracketed placeholders with your details and have a qualified attorney review it before relying on it.
        </div>

        <div className="mt-8 space-y-8">
          {SECTIONS.map((s) => (
            <section key={s.h} data-testid={`privacy-section`}>
              <h2 className="font-display text-lg font-semibold">{s.h}</h2>
              {s.p.map((para, i) => (
                <p key={i} className="mt-2 text-sm leading-relaxed text-muted-foreground">{para}</p>
              ))}
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}
